// src/catalog/hooks/useCatalogPathwayResolution.js

import { useEffect, useState } from "react";

const apiUrl =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

function getAuthToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken")
  );
}

function safePathwayResolutionMessage(result, status) {
  if (status === 401 || result?.reasonCode === "authentication_required") {
    return "Please log in to open this catalog entry.";
  }

  if (result?.reasonCode === "resource_hidden") {
    return "Catalog entry not found.";
  }

  if (
    result?.reasonCode === "resource_closed" ||
    result?.reasonCode === "resource_archived"
  ) {
    return "This catalog entry is not available.";
  }

  if (
    result?.reasonCode === "decision_service_unavailable" ||
    result?.effect === "indeterminate" ||
    status >= 500
  ) {
    return "This catalog entry could not be opened right now.";
  }

  return "You do not have access to this catalog entry.";
}

export default function useCatalogPathwayResolution(
  slug,
  { enabled = true, launchConfigurationId = null } = {}
) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState("");

  const endpoint = `${apiUrl}/catalog/app/${slug}/open${
    launchConfigurationId
      ? `?launchConfig=${encodeURIComponent(launchConfigurationId)}`
      : ""
  }`;

  useEffect(() => {
    let cancelled = false;

    async function resolveCatalogPathway() {
      try {
        if (!enabled) {
          setResult(null);
          setLoading(false);
          setError("");
          return;
        }

        setLoading(true);
        setError("");

        if (!slug) {
          throw new Error("Catalog pathway slug is required");
        }

        const token = getAuthToken();

        if (!token) {
          throw new Error(
            "Catalog pathway request failed: missing auth token"
          );
        }

        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (!cancelled) {
          setResult(data);

          if (!response.ok || data?.authorized !== true) {
            setError(safePathwayResolutionMessage(data, response.status));
          }
        }
      } catch (err) {
        if (!cancelled) {
          setResult(null);
          setError(
            "This catalog entry could not be opened right now."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    resolveCatalogPathway();

    return () => {
      cancelled = true;
    };
  }, [enabled, endpoint, slug]);

  return {
    result,
    loading,
    error
  };
}
